import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';

import {ModalDialogDismissOption} from '@shared/confirmation-dialog/modal-dialog-dismiss-option';
import {ApiConfigurationService} from '@services/administration/machine/trigger/api-configuration.service';
import {NotificationService} from '@services/notification/notification.service';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';

import {Machine} from '@models/machine/machine';
import {TriggerQuery} from '@models/adminstration/machine/trigger/trigger-query/trigger-query';
import {ApiConfiguration} from '@models/machine/api-configuration';
import {ApiConfigurationDetails} from '@models/machine/api-configuration-details';

import {ALPHANUMERIC_REGEX} from '@constants/reg-exp';
import {Role} from '@models/user/role';
import {User} from '@models/user/user';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

@Component({
    selector: 'scitis-admin-api-configuration-edit',
    templateUrl: './api-configuration-edit.component.html',
    styleUrls: ['./api-configuration-edit.component.scss'],
    standalone: false
})
export class ApiConfigurationEditComponent implements OnInit, OnDestroy {

  @Input()
  machine: Machine = new Machine();

  @Input()
  apiConfigs: ApiConfigurationDetails[] = [];

  @Input()
  userRole: Role = new Role(new User());

  @Input()
  endpoint: ApiConfigurationDetails | undefined = undefined;

  submitted: boolean = false;
  baseUrl: string = '';
  isLoadingAvailableTriggerQueries = false;
  superOrTechAdmin = false;
  endpointId: number = -1;
  availableTriggerQueries: TriggerQuery[] = [];
  form: UntypedFormGroup = new UntypedFormGroup({});

  private CURRENT_PAGE_NAME: string = 'ApiConfigurationEditComponent';
  private destroy: Subject<boolean> = new Subject<boolean>();

  constructor(
      private activeModal: NgbActiveModal,
      private apiConfigurationService: ApiConfigurationService,
      private translateService: TranslateService,
      private notificationService: NotificationService,
      private loadingIndicator: LoadingIndicatorService) {
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.form.controls;
  }

  get isDisabledSaveBtn(): boolean {
    return this.submitted ? this.form.invalid : this.submitted && this.form.invalid;
  };

  ngOnInit(): void {
    this.setBaseUrl();
    this.setAvailableQueries();
    if (this.endpoint !== undefined && this.endpoint.id !== undefined) {
      this.endpointId = this.endpoint.id;
    }
    this.generateForm();
    this.superOrTechAdmin = this.userRole.isSuperAdminRole || this.userRole.isTechAdminRole;
  }

  onClose(): void {
    this.activeModal.dismiss(ModalDialogDismissOption.CLOSE);
  }

  onCancel(): void {
    this.activeModal.dismiss(ModalDialogDismissOption.CANCEL);
  }

  onUrlNameChange() {
    if (this.f.url.value !== null && this.f.url.value !== undefined && this.f.url.value !== '' &&
        (!this.f.url.errors || !this.f.url.errors.pattern)) {
      if (this.f.url.errors && this.f.url.errors.used) {
        this.form.controls.url.setErrors({used: false});
      }
      this.apiConfigs.forEach(u => {
        if (u.url.toUpperCase() === this.f.url.value.toUpperCase()) {
          this.form.controls.url.setErrors({used: true});
        }
      });
    } else {
      if (this.f.url.errors && this.f.url.errors.used) {
        this.f.url.setErrors({used: false});
      }
    }
  }

  onSave(): void {
    this.submitted = true;

    if (this.form.invalid) {
      return;
    }

    this.saveApiEndpoint();
  }

  private setBaseUrl(): void {
    this.baseUrl = this.apiConfigurationService.getEndpointUrl(this.machine, '');
  }

  private setAvailableQueries(): void {
    if (this.machine.id !== undefined) {
      this.isLoadingAvailableTriggerQueries = true;
      this.apiConfigurationService.getAvailableQueries(this.machine.id).pipe(takeUntil(this.destroy)).subscribe({
        next: (triggerQueries: TriggerQuery[]) => {
          this.availableTriggerQueries = triggerQueries;
          if (this.endpoint !== undefined) {
            this.availableTriggerQueries.push(this.endpoint.triggerQuery);
          }
          this.isLoadingAvailableTriggerQueries = false;
        }, error: error => {
          console.log(error);
          this.isLoadingAvailableTriggerQueries = false;
          const message = this.translateService.instant('Error loading trigger actions');
          this.notificationService.error(message, error, this.CURRENT_PAGE_NAME, true);
        }
      });
    }
  }

  private generateForm(): void {
    const url = this.endpoint !== undefined ? this.endpoint.url : '';
    const action = this.endpoint?.triggerQuery.id !== undefined ? this.endpoint.triggerQuery.id : '';
    const maxRequestsPerHour = this.endpoint !== undefined ? this.endpoint.maxRequestsPerHour : 60;

    this.form = new UntypedFormGroup({
      url: new UntypedFormControl(url, [Validators.required, Validators.pattern(ALPHANUMERIC_REGEX)]),
      action: new UntypedFormControl(action, [Validators.required]),
      maxRequestsPerHour: new UntypedFormControl(maxRequestsPerHour, [Validators.required, Validators.min(1)])
    });
  }

  private saveApiEndpoint(): void {
    this.loadingIndicator.show(this.translateService.instant('Loading'));
    const triggerQueryId = this.getQueryId();
    this.apiConfigurationService.saveApiEndpoint(this.machine.id, this.endpointId, 'false', this.f.maxRequestsPerHour.value, triggerQueryId,
        this.f.url.value,).pipe(takeUntil(this.destroy)).subscribe({
      next: (apiConfiguration: ApiConfiguration) => {
        this.activeModal.close(apiConfiguration);
        const message = this.translateService.instant('Api endpoint saved');
        this.notificationService.success(message, this.CURRENT_PAGE_NAME, false);
        this.loadingIndicator.hide();
      }, error: error => {
        console.log(error);
        this.loadingIndicator.hide();
        const message = this.translateService.instant('Error saving api configuration');
        this.notificationService.error(message, error, this.CURRENT_PAGE_NAME, true);
      }
    });
  }

  private getQueryId(): number {
    let triggerQueryId = this.f.action.value;
    if (this.endpoint !== undefined && this.f.action.value === this.endpoint.triggerQuery.name) {
      triggerQueryId = this.endpoint.triggerQuery.id;
    }
    return triggerQueryId;
  }

  ngOnDestroy() {
    this.destroy.next(true);
    this.destroy.complete();
  }
}
